import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

export function useUsageBalance() {
  return useQuery({
    queryKey: ["/api/usage/balance"],
    queryFn: () =>
      fetchJson<{ balance: number; plan: any; usedThisCycle: number; hasStripeSubscription?: boolean }>("/api/usage/balance"),
    staleTime: 60_000,
  });
}

export function useUsageActions(limit = 20) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["/api/usage/actions", limit],
    queryFn: () => fetchJson<{ rows: any[] }>(`/api/usage/actions?limit=${limit}`),
    refetchInterval: query => query.state.data?.rows.some(row => row.imageRefundReviews?.some((review: any) => review.status === "pending")) ? 5000 : false,
  });
  useEffect(() => {
    if (query.data?.rows.some(row => Number(row.creditsRefunded) > 0 || row.imageRefundReviews?.some((review: any) => review.status === "refunded"))) {
      queryClient.invalidateQueries({ queryKey: ["/api/usage/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/usage/brand-breakdown"] });
    }
  }, [query.dataUpdatedAt, queryClient]);
  return query;
}

export function useBrandBreakdown() {
  return useQuery({
    queryKey: ["/api/usage/brand-breakdown"],
    queryFn: () => fetchJson<{ rows: any[] }>("/api/usage/brand-breakdown"),
  });
}

export function usePublicPlans() {
  return useQuery({
    queryKey: ["/api/usage/plans"],
    queryFn: () => fetchJson<{ rows: any[] }>("/api/usage/plans"),
  });
}

export function useReviewImageRefunds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: number) => {
      const response = await fetch(`/api/usage/actions/${actionId}/review-image-refunds`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      if (!response.ok) throw new Error("Billing review could not be queued. Please try again later.");
      return response.json() as Promise<{queued: number; status: string}>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/usage/actions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/usage/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/usage/brand-breakdown"] });
    },
  });
}
